import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { formatDollarsWholeNumber } from "common/utils/formatDollars";
import { masterPlanLibraryPath } from "lib/routeHelpers";
import { useState } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";
import type { TreeSelectNodeType } from "ui/components/TreeSelect";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import styles from "./MasterPlanReviewModal.module.scss";
import MasterPlanReviewSummary from "./MasterPlanReviewSummary";

interface MasterPlanReviewModalProps {
  modalProps: ModalProps;
  onBack: () => void;
  onSubmit: (setIsSaving?: (saving: boolean) => void) => void;
  studioFileName?: string;
  planName: string;
  basePrice?: number;
  treeData: TreeSelectNodeType[];
  selectedIds: string[];
  createdPlan?: ClientMasterPlan;
  builderId?: string;
  /** Optional: leaf-level selected IDs for filtering */
  leafSelectedIds?: string[];
  /** Optional: use "X new location(s)" header format */
  useNewLocationsLabel?: boolean;
  /** Optional: custom modal title */
  title?: string;
  /** Optional: custom submit button text */
  submitButtonText?: string;
  /** Optional: array of master plans for bulk operations */
  masterPlans?: ClientMasterPlan[];
  /** Optional: map of master plan IDs to location IDs that have duplicates */
  duplicates?: Record<string, string[]>;
}

export default function MasterPlanReviewModal({
  modalProps,
  onBack,
  onSubmit,
  studioFileName,
  planName,
  basePrice,
  treeData,
  selectedIds,
  createdPlan,
  builderId,
  useNewLocationsLabel = false,
  title = "Review Master Plan",
  submitButtonText = "Create",
  masterPlans,
  duplicates,
}: MasterPlanReviewModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useAppNavigate();

  return (
    <Modal
      {...modalProps}
      size={createdPlan ? "small" : modalProps.size ?? "medium"}
      title={createdPlan ? "Master Plan Created" : title}
      variants={createdPlan ? ["bodyPadding"] : ["bodyPadding", "footerButtons"]}
      submitting={isSaving}
      scrollModal={!createdPlan}
      footer={
        !createdPlan ? (
          <>
            <Button btnSize="large" btnStyle="tertiary" disabled={isSaving} onClick={onBack}>
              Back
            </Button>
            <Button
              btnSize="large"
              onClick={() => {
                if (!isSaving) {
                  setIsSaving(true);
                  onSubmit(setIsSaving);
                }
              }}
              isPending={isSaving}
            >
              {submitButtonText}
            </Button>
          </>
        ) : undefined
      }
    >
      {createdPlan ? (
        <div className={styles.successWrapper}>
          <div className={styles.successName}>{createdPlan.name}</div>
          <div className={styles.successSubtitle}>Your new Master Plan is ready.</div>
          <Button
            className={styles.successButton}
            btnSize="large"
            onClick={() => {
              if (!builderId || !createdPlan?.id) {
                return;
              }
              void navigate({ to: masterPlanLibraryPath(builderId, createdPlan.id) });
            }}
          >
            Configure Options
          </Button>
        </div>
      ) : (
        <div className={styles.wrapper}>
          {studioFileName !== undefined && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>Studio File</h4>
              <div className={styles.value}>{studioFileName ?? "—"}</div>
            </section>
          )}
          {masterPlans?.length ? (
            <MasterPlanReviewSummary
              plans={masterPlans}
              treeData={treeData}
              selectedLocationIds={selectedIds}
              duplicates={duplicates}
              useNewLocationsLabel={useNewLocationsLabel}
            />
          ) : (
            <>
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Plan Details</h4>
                <div className={styles.details}>
                  <div className={styles.value}>{planName}</div>
                  {basePrice !== undefined && <div className={styles.value}>{formatDollarsWholeNumber(basePrice)}</div>}
                </div>
              </section>
              <MasterPlanReviewSummary
                plans={[]}
                treeData={treeData}
                selectedLocationIds={selectedIds}
                useNewLocationsLabel={useNewLocationsLabel}
              />
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
